import { useState } from "react";
import PhotoPlaceholder from "./PhotoPlaceholder";
import useCharacterPhoto from "../hooks/useCharacterPhoto";

/**
 * Bio card with intentionally expandable text area — when real biographies
 * arrive, the card will grow naturally without breaking the page grid.
 *
 * Photos come from Emergent object storage via `/api/photos`. When no photo
 * has been uploaded for a slug, we fall back to <PhotoPlaceholder>.
 */
export default function BioCard({ slug, name, role, intro, photoLabel, testId, accent }) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const photoRelUrl = useCharacterPhoto(slug);
  const photoUrl = photoRelUrl
    ? (photoRelUrl.startsWith("http") ? photoRelUrl : `${backendUrl}${photoRelUrl}`)
    : null;
  const showPhoto = photoUrl && !photoFailed;

  return (
    <article
      data-testid={testId || `bio-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className="relative group flex flex-col h-full bg-[#F9F6F0] border border-[#E7DFD5] rounded-md page-shadow overflow-hidden transition-all duration-700 hover:-translate-y-[2px]"
    >
      {accent && (
        <div
          className="absolute top-0 left-0 right-0 h-1.5"
          style={{ background: accent }}
        />
      )}
      <div className="p-5 pb-3">
        {showPhoto ? (
          <div
            data-testid={`photo-${slug}`}
            className="relative aspect-square w-full overflow-hidden"
          >
            {/* outer frame — same sage-green / accent trim as the placeholder */}
            {accent ? (
              <div
                className="absolute inset-0 rounded-sm shadow-md"
                style={{
                  background: `linear-gradient(180deg, ${accent} 0%, ${accent}cc 100%)`,
                }}
              />
            ) : (
              <div className="absolute inset-0 wood rounded-sm shadow-md" />
            )}
            {/* inner photo */}
            <img
              src={photoUrl}
              alt={`${name} — ${role}`}
              loading="lazy"
              onError={() => setPhotoFailed(true)}
              className="absolute inset-[10px] w-[calc(100%-20px)] h-[calc(100%-20px)] object-cover rounded-[2px] border border-[#8D6E63]/30"
              draggable="false"
            />
          </div>
        ) : (
          <PhotoPlaceholder label={`${photoLabel || name} Photo`} aspect="square" accent={accent} />
        )}
      </div>
      <div className="px-7 pb-8 pt-2 flex flex-col flex-grow text-center">
        <p
          className={`text-xs uppercase tracking-[0.25em] mb-2 ${/^\s*coming soon\s*$/i.test(role || "") ? "coming-soon-flash" : ""}`}
          style={{ color: accent || "#4B3A2F" }}
        >
          {role}
        </p>
        <h3 className="font-display text-3xl text-[#4B3A2F] mb-3">{name}</h3>
        <div className="gold-rule mb-4" />
        {/* expandable text body */}
        <p className="font-body text-base md:text-[1.02rem] leading-relaxed text-[#2C2724]/85 flex-grow">
          {intro}
        </p>
        <p className="mt-6 font-script text-xl text-[#4B3A2F]">
          more of {name.split(" ")[0]}&apos;s story{" "}
          <span className="coming-soon-flash">coming soon</span>
        </p>
      </div>
    </article>
  );
}
